import type {QueryClient} from "@tanstack/react-query";
import {z as zod} from "zod";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {uniqueUuid} from "@vesta/unique/unique";
import {WorkspaceCapabilityStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

export const WORKSPACE_CAPABILITIES_BY_TYPE_QUERY_KEY = uniqueUuid`e8a1b2c3-d4e5-4f6a-8b9c-0d1e2f3a4b5c`;

const workspaceCapabilitySummaryValidator = zod.object({
    id: uuidZodValidator,
    status: zod.enum(WorkspaceCapabilityStatus),
    type: zod.enum(WorkspaceCapabilityType),
    updatedAt: iso8601DateTimeZodValidator,
    workspaceId: uuidZodValidator,
});

export type WorkspaceCapabilitySummary = zod.infer<typeof workspaceCapabilitySummaryValidator>;

export async function fetchWorkspaceCapabilitiesByType({
    intellsysBaseUrl,
    accessToken,
    type,
    status,
}: {
    intellsysBaseUrl: string;
    accessToken: string;
    type: WorkspaceCapabilityType;
    status?: WorkspaceCapabilityStatus;
}): Promise<Array<WorkspaceCapabilitySummary>> {
    const result = await robustJsonResultClientFetch(
        `${intellsysBaseUrl}/sub-system/pluto/admin/workspace-capabilities/list-by-type`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                type,
                ...(status === undefined ? {} : {status}),
            }),
        },
        zod.array(workspaceCapabilitySummaryValidator),
    );
    if (result.success !== true) {
        throw result.error;
    }
    return result.data;
}

export async function getWorkspaceCapabilitiesByTypeWithCaching({
    queryClient,
    intellsysBaseUrl,
    accessToken,
    type,
    status,
}: {
    queryClient: QueryClient;
    intellsysBaseUrl: string;
    accessToken: string;
    type: WorkspaceCapabilityType;
    status?: WorkspaceCapabilityStatus;
}): Promise<Array<WorkspaceCapabilitySummary>> {
    return queryClient.ensureQueryData({
        queryKey: [WORKSPACE_CAPABILITIES_BY_TYPE_QUERY_KEY, type, status ?? null],
        queryFn: async () =>
            fetchWorkspaceCapabilitiesByType({
                intellsysBaseUrl,
                accessToken,
                type,
                status,
            }),
        retry: false,
        staleTime: 30_000,
    });
}
